import { get } from 'svelte/store';
import { user_data, user_settings } from 'src/stores/account/user';
import { real_id } from 'src/helpers/interface_helpers';
import type { Device } from 'src/types/Device';
import type { Group } from 'src/types/Group';

import { isDevServerUser, isRepoUser, isT1User } from 'src/helpers/user_server_conditions';

export const isPremiumUser = (): boolean => {
	return get(user_data).premium > 0 || import.meta.env.DEV;
};

export const isTestUser = (): boolean => {
	return isDevServerUser(['repo', 'nb']) || isT1User() || import.meta.env.DEV;
};

export const checkUser = () => {
	return get(user_data)?.email !== '';
};

/** @deprecated Use `isSubUnit` instead */
export const isSubAccount = () => {
	return typeof get(user_settings)?.sub === 'number';
};

export const isSubUnit = isSubAccount;

// Home owners (non-sub sessions) always have full rights. Shared members are
// gated per-right by the `rights` tuple, where `0` means the right was revoked.
const hasDeviceRight = (device, right: 'control' | 'settings'): boolean =>
	!get(user_settings)?.sub || device?.rights?.[right] !== 0;

export const hasControlRights = (device) => hasDeviceRight(device, 'control');

export const hasSettingsRights = (device) => hasDeviceRight(device, 'settings');

/**
 * Determines whether the current user can control a group, mirroring
 * `hasControlRights` for single devices. In a shared home without control
 * access every member device has `rights.control === 0`, so the group controls
 * must be disabled. Home owners (non-sub units) always retain control.
 *
 * @param group - Group whose member devices are evaluated
 * @param devicesMap - Devices store value used to resolve member device rights
 * @returns true when control is allowed (owner, empty group, or at least one controllable device)
 */
export const hasGroupControlRights = (
	group: Pick<Group, 'devices'> | undefined,
	devicesMap: Record<string, Device>,
): boolean => {
	if (!get(user_settings)?.sub) return true;
	if (!group?.devices?.length) return true;
	return group.devices.some((deviceId) => hasControlRights(devicesMap?.[real_id(deviceId)]));
};

export const hasAlarmControlRights = (alarm) => {
	return !get(user_settings)?.sub || alarm?._meta?.rights?.enable !== 0;
};
/**
 * Determines the user's premium status and whether they have set the weather station.
 *
 * @param {boolean} isPremium - Indicates if the user is currently a premium user.
 * @param {boolean} wasPremiumBefore - Indicates if the user was a premium user before.
 * @param {boolean} [hasWeatherStation] - Optional. Indicates if the user has set the weather station.
 * @returns {Object} An object containing the user's premium status and, if provided, the weather station status.
 */
function checkPremiumUserStatus(isPremium, wasPremiumBefore, hasWeatherStation = null) {
	let premiumStatus;
	if (isPremium) {
		premiumStatus = 'User is premium';
	} else if (wasPremiumBefore) {
		premiumStatus = 'User is not premium now but was premium before';
	} else {
		premiumStatus = 'User is not premium';
	}

	const result = { premiumStatus: premiumStatus, weatherStationStatus: '' };

	if (typeof hasWeatherStation !== null) {
		let weatherStationStatus;
		if (hasWeatherStation) {
			weatherStationStatus = 'User has set the weather station';
		} else {
			weatherStationStatus = 'User has not set the weather station';
		}
		result.weatherStationStatus = weatherStationStatus;
	}

	return result;
}

// console.log(checkPremiumUserStatus(true, false, true))

/**
 * Checks the premium user status based on the given status type.
 *
 * @param {string} status - The type of status to check. Possible values are:
 *                          'isPremium', 'beenPremium', 'hasWeatherStation'.
 * @returns {boolean|undefined} - Returns a boolean value based on the status type:
 *                                - 'isPremium': Returns true if the user is currently a premium user, otherwise false.
 *                                - 'beenPremium': Returns the value indicating whether the user has ever been a premium user.
 *                                - 'hasWeatherStation': Returns true if the user has a weather station, otherwise false.
 *                                - Returns undefined if the status type is not recognized.
 */
function checkPremiumUserStatus2(status) {
	switch (status) {
		case 'isPremium':
			return !!get(user_data).premium;

		case 'beenPremium':
			return get(user_settings).been_premium;

		case 'hasWeatherStation':
			return typeof get(user_settings).weather_station.l !== 'undefined' ? true : false;
		default:
			break;
	}
}

// console.log(checkPremiumUserStatus2('hasWeatherStation'))

export const getVoucherUrl = (): string => {
	if (isTestUser()) {
		if (isRepoUser()) {
			return import.meta.env.VITE_REPO_VOUCHER_URL_TEST;
		} else {
			return import.meta.env.VITE_VOUCHER_URL_TEST;
		}
	} else {
		if (isRepoUser()) {
			return import.meta.env.VITE_REPO_VOUCHER_URL;
		} else {
			return import.meta.env.VITE_VOUCHER_URL;
		}
	}
};
